import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

const inputCls =
  'bg-[#0a1628]/60 border-white/15 text-white placeholder:text-white/45 focus-visible:ring-[#00b4d8] focus-visible:ring-offset-0 rounded-xl h-12 px-4';

export const TourInquiryForm = ({
  tripSlug,
  tripName,
  title = 'Book a Tour with Us',
  subtitle,
}) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Please enter your name.');
    if (!form.phone.trim()) return setError('Please enter a phone number.');
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return setError('Please enter a valid email.');
    setSubmitting(true);
    try {
      const { data } = await api.post('/bookings/inquiry', {
        trip_slug: tripSlug,
        trip_name: tripName,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setSent({ ref: data?.inquiry_ref });
      setForm({ name: '', phone: '', email: '', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send your inquiry. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not send your inquiry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="tour-inquiry-section"
      className="relative overflow-hidden rounded-3xl bg-[#0a1f4a] border border-white/8 p-6 sm:p-10 lg:p-14"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        background:
          'radial-gradient(60% 50% at 100% 0%, rgba(0,180,216,0.18) 0%, transparent 60%), radial-gradient(50% 50% at 0% 100%, rgba(200,230,0,0.12) 0%, transparent 60%)',
      }} />

      <div className="relative">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">{title}</h2>
        {subtitle && (
          <p className="mt-4 text-base sm:text-lg text-white/75 max-w-2xl whitespace-pre-line">{subtitle}</p>
        )}

        {sent ? (
          <div data-testid="tour-inquiry-success" className="mt-8 rounded-2xl border border-[#c8e600]/30 bg-[#c8e600]/[0.08] p-5 flex items-start gap-3 max-w-2xl">
            <CheckCircle2 className="h-6 w-6 text-[#c8e600] shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg font-semibold text-white">Thanks — we received your inquiry!</div>
              <p className="mt-1 text-sm text-white/75">
                Reference <span className="font-mono text-[#c8e600]">{sent.ref}</span>. Our team will get back to you shortly.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 max-w-3xl space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                data-testid="inquiry-name"
                placeholder="Name"
                className={inputCls}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                autoComplete="name"
              />
              <Input
                data-testid="inquiry-phone"
                placeholder="Phone"
                className={inputCls}
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                type="tel"
                autoComplete="tel"
              />
            </div>
            <Input
              data-testid="inquiry-email"
              placeholder="Email"
              className={inputCls}
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              type="email"
              autoComplete="email"
            />
            <Textarea
              data-testid="inquiry-message"
              placeholder="Message"
              rows={5}
              className={`${inputCls} h-auto py-3 resize-y`}
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
            />

            {error && (
              <div data-testid="inquiry-error" className="rounded-xl border border-red-400/30 bg-red-400/10 text-red-200 text-sm px-4 py-2">{error}</div>
            )}

            <div>
              <Button
                type="submit"
                data-testid="inquiry-submit"
                disabled={submitting}
                className="bg-[#3ad6e0] hover:bg-[#21c0cb] text-[#0a1628] font-semibold rounded-md h-14 px-12 text-base disabled:opacity-60"
              >
                {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</>) : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
